import React, { useMemo, useCallback } from "react";
import { z } from "zod";
import { T } from "@transifex/react";
import { Button } from "storybook/components/button";
import { Modal } from "storybook/components/modal";
import { Typography } from "storybook/components/typography";
import { useForm } from "storybook/utils/form";
import {
  ErrorType,
  ModalType,
  usePatientInterest,
} from "context/patient-interest";
import { AvailabilityPicker } from "components/availability-picker";
import { FormSection } from ".";
import { ErrorBanner, ModalBodyWrapper } from "../styles";
import { ButtonsContainer } from "./styles";

const dayAvailability = z.object({
  enabled: z.boolean().default(false),
  am: z.boolean().default(false),
  pm: z.boolean().default(false),
});

export const availabilitySchema = z.object({
  anytime: z.boolean().default(false),
  monday: dayAvailability,
  tuesday: dayAvailability,
  wednesday: dayAvailability,
  thursday: dayAvailability,
  friday: dayAvailability,
});

export type AvailabilityFormData = z.infer<typeof availabilitySchema>;
export type WeekDay =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday";

type Props = {
  availability?: AvailabilityFormData;
  onClose: () => void;
  isSubmitting: boolean;
  onContinue: (data: unknown, formSection: FormSection) => void;
};

export function AvailabilityForm({
  availability,
  onClose,
  onContinue,
  isSubmitting,
}: Props) {
  const initialValues = useMemo(
    () =>
      availability || {
        anytime: false,
        monday: { enabled: false, am: false, pm: false },
        tuesday: { enabled: false, am: false, pm: false },
        wednesday: { enabled: false, am: false, pm: false },
        thursday: { enabled: false, am: false, pm: false },
        friday: { enabled: false, am: false, pm: false },
      },
    [availability]
  );

  const { errorType, setModalType } = usePatientInterest();

  const { Form, watch } = useForm<AvailabilityFormData>({
    schema: availabilitySchema,
    values: initialValues,
  });

  const isAnytimeEnabled = watch("anytime");
  const watchedDays = watch([
    "monday.enabled",
    "tuesday.enabled",
    "wednesday.enabled",
    "thursday.enabled",
    "friday.enabled",
  ]);

  const handleContinue = useCallback(
    (formData: AvailabilityFormData) => {
      setModalType(ModalType.CONFIRMATION);
      onContinue({ availability: formData }, FormSection.AVAILABILITY);
    },
    [onContinue, setModalType]
  );

  const hasAvailability = useMemo(() => {
    if (isAnytimeEnabled) return true;
    return watchedDays.some(Boolean);
  }, [isAnytimeEnabled, watchedDays]);

  return (
    <Form onSubmit={handleContinue}>
      <Modal.Body>
        <ModalBodyWrapper className="notranslate">
          {errorType === ErrorType.GENERAL && (
            <ErrorBanner role="alert">
              <T _str="Something went wrong, please try again" />
            </ErrorBanner>
          )}
          <div>
            <Typography theme="default" variant="body1" color="black">
              <T _str="Please select your availability for appointments." />
            </Typography>
            <div style={{ marginTop: "16px" }}>
              <AvailabilityPicker
                theme="default"
                noContainer={true}
              />
            </div>
          </div>
        </ModalBodyWrapper>
      </Modal.Body>
      <Modal.Actions alignButtons="right">
        <ButtonsContainer className="notranslate">
          <Button
            color="default"
            variant="text"
            theme="default"
            onClick={onClose}
          >
            <T _str="Cancel" />
          </Button>
          <Button
            type="submit"
            color="default"
            variant="contained"
            theme="default"
            disabled={!hasAvailability || isSubmitting}
          >
            <T _str="Done" />
          </Button>
        </ButtonsContainer>
      </Modal.Actions>
    </Form>
  );
}
